<script setup>
import { ref } from "vue";
import helpSmallActive from "assets/graphics/icons/nav-menu/cb_help_small-active.svg";
import helpSmallBlackActive from "assets/graphics/icons/nav-menu/cb_help_small-black-active.svg";
import helpSmallWhite from "assets/graphics/icons/nav-menu/cb_help_small-white.svg";
import helpSmallBlack from "assets/graphics/icons/nav-menu/cb_help_small.svg";
import homeSmallActive from "assets/graphics/icons/nav-menu/cb_home_small-active.svg";
import homeSmallWhite from "assets/graphics/icons/nav-menu/cb_home_small-white.svg";
import homeSmall from "assets/graphics/icons/nav-menu/cb_home_small.svg";
import settingsSmallActive from "assets/graphics/icons/nav-menu/cb_settings_small-active.svg";
import settingsSmallWhite from "assets/graphics/icons/nav-menu/cb_settings_small-white.svg";
import settingsSmallBlack from "assets/graphics/icons/nav-menu/cb_settings_small.svg";
import infoCircleActive from "assets/graphics/icons/nav-menu/info-circle-active.svg";
import infoCircleWhite from "assets/graphics/icons/nav-menu/info-circle-white.svg";
import infoCircle from "assets/graphics/icons/nav-menu/info-circle.svg";
import cbNewsSmall from "~/assets/graphics/icons/cb_news_small.svg";
import cbPlus from "~/assets/graphics/icons/cb_plus_small.svg";
import cbUser from "~/assets/graphics/icons/cb_user_d1.svg";
import cbActivitySmallActive from "~/assets/graphics/icons/nav-menu/cb_activity_small-active.svg";
import cbActivitySmall from "~/assets/graphics/icons/nav-menu/cb_activity_small.svg";
import cbContactSmallActive from "~/assets/graphics/icons/nav-menu/cb_contact_small-active.svg";
import cbContactSmall from "~/assets/graphics/icons/nav-menu/cb_contact_small.svg";
import cbFoodSmallActive from "~/assets/graphics/icons/nav-menu/cb_food_small-active.svg";
import cbFoodSmall from "~/assets/graphics/icons/nav-menu/cb_food_small.svg";
import cbNews2Active from "~/assets/graphics/icons/nav-menu/cb_news2-active.svg";
import cbNews2 from "~/assets/graphics/icons/nav-menu/cb_news2.svg";
import cbPicsSmallActive from "~/assets/graphics/icons/nav-menu/cb_pics_small-active.svg";
import cbPicsSmall from "~/assets/graphics/icons/nav-menu/cb_pics_small.svg";
import cbResidenceSmallActive from "~/assets/graphics/icons/nav-menu/cb_residence_small-active.svg";
import cbResidenceSmall from "~/assets/graphics/icons/nav-menu/cb_residence_small.svg";
import cbTimeSmallActive from "~/assets/graphics/icons/nav-menu/cb_time_small-active.svg";
import cbTimeSmall from "~/assets/graphics/icons/nav-menu/cb_time_small.svg";

const isHovered = ref(false);

const props = defineProps({
  to: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  text: {
    type: String,
    required: true
  },
  indent: {
    type: Boolean,
    required: false,
    default: false
  },
  highlightSubPaths: {
    type: Boolean,
    required: false,
    default: false
  },
  isChip: {
    type: Boolean,
    required: false,
    default: false
  }
});

const route = useRoute();
const isHighlighted = computed(() => {
  if (props.highlightSubPaths) {
    return route.fullPath.startsWith(props.to);
  }

  if (props.to === "/" && Object.keys(route.query).length > 0) {
    return true;
  }

  return route.fullPath === props.to;
});

const getImage = computed(() => {
  const active = isHighlighted.value || isHovered.value;
  switch (props.icon) {
    case "cb_activity_small.svg":
      return active ? cbActivitySmallActive : cbActivitySmall;
    case "cb_news_small.svg":
      return cbNewsSmall;
    case "cb_news2.svg":
      return active ? cbNews2Active : cbNews2;
    case "cb_time_small.svg":
      return active ? cbTimeSmallActive : cbTimeSmall;
    case "cb_contact_small.svg":
      return active ? cbContactSmallActive : cbContactSmall;
    case "cb_food_small.svg":
      return active ? cbFoodSmallActive : cbFoodSmall;
    case "cb_pics_small.svg":
      return active ? cbPicsSmallActive : cbPicsSmall;
    case "cb_residence_small.svg":
      return active ? cbResidenceSmallActive : cbResidenceSmall;
    case "settingsSmall":
      return props.isChip
        ? settingsSmallBlack
        : active
          ? settingsSmallActive
          : settingsSmallWhite;
    case "helpSmall":
      if (props.isChip) {
        return active ? helpSmallBlackActive : helpSmallBlack;
      } else {
        return active ? helpSmallActive : helpSmallWhite;
      }
    case "info_circle":
      return props.isChip
        ? infoCircle
        : active
          ? infoCircleActive
          : infoCircleWhite;
    case "homeSmall":
      return props.isChip ? homeSmall : active ? homeSmallActive : homeSmallWhite;
    case "user":
      return cbUser;
    case "plus":
      return cbPlus;
    default:
      return cbActivitySmall;
  }
});
</script>

<template>
  <li>
    <nuxt-link
      :to="to"
      :class="[
        'navigation-wrapper',
        {
          'navigation-active': isHighlighted,
          'navigation-link': !isHighlighted,
          'navigation-indent': indent,
          'is-chip': props.isChip,
        },
      ]"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <img class="navigation-img" :src="getImage">
      <span>{{ text }}</span>
    </nuxt-link>
  </li>
</template>

<style scoped>
.navigation-wrapper {
  height: 3.5rem;
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  border-radius: 8px;
}

.navigation-wrapper img {
  height: 1.25rem;
}

.navigation-wrapper span {
  padding-top: 0.25rem;
}

.is-chip.navigation-active {
  color: black !important;
  background-color: var(--ct_D1);
}

.navigation-active {
  color: var(--ct_D1);
  text-decoration: none !important;
  text-transform: capitalize;
}

.navigation-active:hover {
  color: var(--ct_D1);
  text-decoration: none;
}

.navigation-link {
  color: var(--ct_E1);
  text-decoration: none !important;
  text-transform: capitalize;
  display: flex;
}

.navigation-link:hover {
  color: var(--ct_D1);
  text-decoration: none;
}

.navigation-indent {
  margin-left: 30px;
}

@media screen and (max-width: 925px) {
  .navigation-wrapper {
    padding: 1rem 2rem;
  }
}
</style>
